import { BaseListViewStore } from 'core/stores';
import {
	notificationError,
	getErrorMessage,
	translate,
	checkForColumnWidth,
	isSuperAdminOrganization,
} from 'core/utils';
import { observable, action, reaction, makeObservable } from 'mobx';
import { SyncStore } from 'modules/sync/stores';
import { ChannelStore } from 'modules/channel/stores';
import { DateTimeCellData, BooleanToggleCellData, StatusCellData } from 'core/stores';
import { sourceOfChange, channelType } from 'common/constants';
import { localStorageHandler } from 'core/services';
import { IconCellData } from 'core/stores/CellDataStore';
import { UpdateInventoryStore } from 'modules/product/stores';
import { AmazonTemplates, CustomLabel, WarehouseCodes } from '../pages';
import _ from 'lodash';

class ChannelListViewStore extends BaseListViewStore {
	@observable change = false;
	isSuperAdmin = false;
	syncStore = null;
	localStorageWidthKey = 'channels-column-width';

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ChannelStore(rootStore),
			queryParams: {
				embed: ['seller', 'channelType', 'productInventoryRestockDate'],
			},
			findFunction: 'findChannel',
			tableData: {
				columns: [
					{
						key: 'name',
						title: 'CHANNEL.NAME',
						width: false,
						sortable: true,
						dataCell: IconCellData,
						onClick: channel => this.routes.edit(channel.id),
						selected: true,
					},
					{
						key: 'channelType.name',
						width: false,
						sortable: true,
						title: 'CHANNEL.CHANNELTYPE',
						selected: true,
					},
					{
						key: 'lastSyncRealTime',
						title: 'CHANNEL.LASTUPDATETIME',
						width: false,
						dataCell: DateTimeCellData,
						sortable: true,
						dateFormat: 'LLL',
						useLocalTime: true,
						selected: true,
					},
					{
						key: 'seller.applicationTitle',
						width: false,
						sortable: true,
						width: 200,
						title: 'CHANNEL.SELLER',
						selected: true,
						enabled: isSuperAdminOrganization(rootStore),
						filterApiParams: {
							sellerOnly: true,
						},
					},
					{
						key: 'shouldSync',
						title: 'CHANNEL.SHOULDSYNC',
						width: false,
						dataCell: BooleanToggleCellData,
						sortable: false,
						onClick: (channel, field) => this.shouldSyncToggle(channel, field),
						selected: true,
					},
					{
						key: 'updateInventory',
						title: 'CHANNEL.UPLOADINVENTORY',
						width: false,
						dataCell: BooleanToggleCellData,
						sortable: false,
						onClick: (channel, field) => this.updateChannelInventory(channel, field),
						selected: true,
					},
					{
						key: 'lastSyncError',
						width: false,
						sortable: false,
						dataCell: StatusCellData,
						width: 100,
						title: 'CHANNEL.STATUS',
						selected: true,
					},
				],
				sortObject: [
					{
						field: 'name',
						dir: 'asc',
					},
				],
				actions: {
					onEdit: async item => this.routes.edit(item.id),
					onSync: async item => this.openConfirmationModal(this.getSyncConfirmModalProps(item)),
					onDelete: async item => this.openConfirmationModal(this.getDeleteConfirmModalProps(item)),
					onEditAmazonTemplates: async item =>
						this.openModal({
							data: { ...this, selectedItem: item },
							component: AmazonTemplates,
							className: 'ReactModal--lrg ReactModal--vertical',
						}),
					onEditChannelWarehouseCodes: async item =>
						this.openModal({
							data: { ...this, selectedItem: item },
							component: WarehouseCodes,
							className: 'ReactModal--lrg ReactModal--vertical',
						}),
					onEditChannelCustomLabel: async item =>
						this.openModal({
							data: { ...this, selectedItem: item },
							component: CustomLabel,
							className: 'ReactModal--lrg-inter',
						}),
					onUpdateInventoryForChannel: async item =>
						this.openConfirmationModal(this.getUpdateInventoryConfirmModalProps(item)),
				},
			},
			permissions: {
				onEdit: () => this.authStore.hasPermission('channelSection.update'),
				onSync: () => this.authStore.hasPermission('channelSection.create'),
				onDelete: () => this.authStore.hasPermission('channelSection.remove'),
				onEditAmazonTemplates: item =>
					item.channelType.name == channelType.amazon && this.authStore.hasPermission('channelSection.full'),
				onEditChannelWarehouseCodes: () => this.authStore.hasPermission('channelSection.full'),
				onEditChannelCustomLabel: () => this.authStore.hasPermission('channelSection.full'),
				onUpdateInventoryForChannel: item => {
					return (
						isSuperAdminOrganization(this.rootStore) &&
						item.channelType.name == channelType.amazon &&
						!item.useAmazonMli
					);
				},
			},
			routes: {
				detail: id => this.rootStore.routerStore.goTo('master.main.channel.preview', { id }),
				edit: id =>
					this.rootStore.routerStore.goTo('master.app.main.channel.edit', {
						id,
					}),
				create: () => this.rootStore.routerStore.goTo('master.app.main.channel.add', {}),
			},
		});
		makeObservable(this);
		this.initialize();
	}

	@action.bound
	initialize = async () => {
		if (isSuperAdminOrganization(this.rootStore)) {
			this.isSuperAdmin = true;
			await this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		await this.queryUtility.initialize(this.findChannel);
		checkForColumnWidth(localStorageHandler.get(this.localStorageWidthKey), this.tableData.columns);
		this.setButtons();
		this.setSearchVisible();
		this.setHeaderModes();
		reaction(
			() => this.rootStore.routerStore.routerState,
			state => {
				if (state.routeName !== 'master.app.main.channel.list') {
					return;
				}
			}
		);
	};

	@action.bound
	reloadChannels = async () => {
		await this.queryUtility.initialize(false);
	};

	@action.bound
	setAsChanged() {
		this.change = !this.change;
	}

	@action.bound
	setButtons() {
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-plus spc--right--sml',
					onClick: this.routes.create,
					authorization: 'channelSection.create',
					text: 'EDITCHANNELNEW',
				},
			],
		});
	}

	@action.bound
	findChannel = async () => {
		this.suspend();
		this.queryUtility.setFilterParams(this, null, this.tableFilter);
		await this.listStore.findChannel(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.setButtons();
		this.resume();
	};

	@action.bound
	syncChannelOrders = async channel => {
		this.suspend();
		try {
			this.syncStore = new SyncStore(this.rootStore);
			const {
				data: { total, created, updated, canceled },
			} = await this.syncStore.syncByChannelAsync(
				channel.id,
				sourceOfChange.syncSingleChannelOrdersChannels + channel.name
			);
			if (created == 0) {
				this.rootStore.notificationStore.success([
					'CHANNELSYNCNONEWENTRIES',
					{ name: channel.name, updated, canceled },
				]);
			} else {
				this.rootStore.notificationStore.success([
					'CHANNELSYNCSUCCESS',
					{ name: channel.name, total, created, updated, failed: total - (updated + created), canceled },
				]);
			}
		} catch (err) {
			let message = getErrorMessage(err, { name: channel.name });
			const parsedMessage = translate(message);
			if (message[0] === parsedMessage) {
				message = ['CHANNELSYNCERROR.DEFAULTWITHORIGINAL', { name: channel.name, originalMessage: parsedMessage }];
			}
			this.rootStore.notificationStore.error(message);
		}
		this.rootStore.viewStore.getChannels();
		await this.queryUtility.initialize(false);
		this.resume();
	};

	@action.bound
	shouldSyncToggle = async (channel, field) => {
		this.suspend();
		try {
			channel[field] = !channel[field];
			await this.listStore.updateChannel(channel, sourceOfChange.updateChannelShouldSync);
			this.rootStore.notificationStore.success(['LOOKUPUPDATE.CHANNEL', { name: channel.name }]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	updateChannelInventory = async (channel, field) => {
		this.suspend();
		try {
			channel[field] = !channel[field];
			await this.listStore.updateChannel(channel, sourceOfChange.updatechannelInventory);
			this.rootStore.notificationStore.success(['LOOKUPUPDATE.CHANNEL', { name: channel.name }]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action.bound
	updateInventoryForChannel = async () => {
		this.suspend();
		try {
			const channel = this.updateInventoryForChannelConfirmModal.data;
			const result = await new UpdateInventoryStore(this.rootStore).updateInventoryForChannel(
				channel.id,
				sourceOfChange.updatechannelInventory,
				true
			);
			if (result) {
				this.rootStore.notificationStore.success(['CHANNEL.MLIINVENTORYRESETSUCCESS', { name: channel.name }]);
			} else {
				this.rootStore.notificationStore.success(['CHANNEL.MLIINVENTORYRESETFAILED', { name: channel.name }]);
			}
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	@action
	deleteChannel = async channel => {
		this.suspend();
		try {
			await this.listStore.deleteChannel(channel);
			this.rootStore.notificationStore.success(['LOOKUPDELETE.CHANNEL', { name: channel.name }]);
			await this.queryUtility.initialize(false);
		} catch (err) {
			if (err.statusCode === 409 && err.data === 'MESSAGES.CHANNEL.ORDERSALREADYEXIST') {
				this.rootStore.notificationStore.error(err.data);
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
		this.resume();
	};

	getDeleteConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.deleteChannel(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['CHANNELSYNCCONFIRMMODAL', { name: item.name }],
			data: item,
		};
	};

	getSyncConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.syncChannelOrders(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['CHANNELSYNCCONFIRMMODAL', { name: item.name }],
			data: item,
		};
	};

	getUpdateInventoryConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.updateInventoryForChannel(),
			message: ['CHANNEL.UPDATEINVENTORYCONFIRM', { name: item.name }],
			data: item,
		};
	};
}

export default ChannelListViewStore;



// WEBPACK FOOTER //
// ./src/modules/channel/stores/ChannelListViewStore.js